import React, { useMemo } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery } from '@tanstack/react-query';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Shield, ArrowRight, Calendar, TrendingUp, TrendingDown, Minus, Clock, Users, Layers, FileText, Zap, BarChart3 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { format, differenceInDays } from 'date-fns';

const METRIC_DEFS = [
  { key: 'queueGrowth', name: 'Issues Queue Growth Rate', icon: TrendingUp, description: 'Resolution vs. Intake (7 days)' },
  { key: 'noiseReclass', name: 'Noise Reclassification Rate', icon: Layers, description: 'Noise→Signal reclassification %' },
  { key: 'signalMisclass', name: 'Signal Misclassification Rate', icon: Layers, description: 'Balloon→Rock reclassification %' },
  { key: 'bridgingAge', name: 'Bridging Policy Age', icon: FileText, description: 'Avg days for interim policies' },
  { key: 'capCompletion', name: 'Capability Mission Completion', icon: Zap, description: 'Active / total capabilities %' },
  { key: 'balloonTTR', name: 'Time to Resolution (Balloons)', icon: Clock, description: 'Avg days to resolve balloons' },
  { key: 'rockTTM', name: 'Time to First Mitigation (Rocks)', icon: Clock, description: 'Avg days to start Track 3' },
  { key: 'upwardAction', name: 'Upward Report Action Rate', icon: BarChart3, description: 'Sessions with acknowledged reports %' },
  { key: 'officerParticipation', name: 'Officer Reporting Participation', icon: Users, description: 'Distinct reporters (7 days) %' },
];

function getStatus(value, greenTest, amberTest) {
  if (value === null || value === undefined) return 'none';
  if (greenTest(value)) return 'green';
  if (amberTest(value)) return 'amber';
  return 'red';
}

function MetricCardItem({ metric, value, displayValue, targetLabel, status }) {
  const Icon = metric.icon;
  const borderColor = status === 'red' ? 'border-l-red-500' : status === 'amber' ? 'border-l-amber-500' : status === 'green' ? 'border-l-emerald-500' : 'border-l-slate-600';
  const dotColor = status === 'red' ? 'bg-red-500' : status === 'amber' ? 'bg-amber-500' : status === 'green' ? 'bg-emerald-500' : 'bg-slate-600';

  return (
    <Card className={`glass-panel border-slate-800 border-l-4 ${borderColor}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-slate-600" />
          <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">{metric.name}</span>
        </div>
        <div className={`w-2.5 h-2.5 rounded-full ${dotColor}`} />
        </div>
        <div className="text-2xl font-bold text-slate-600 mb-1">{displayValue}</div>
        <p className="text-xs text-slate-600">{targetLabel}</p>
        <p className="text-[10px] text-slate-600 mt-1">{metric.description}</p>
      </CardContent>
    </Card>
  );
}

export default function HighCouncilMetrics({ issues, policies, capabilities, officers, councilSessions }) {
  const now = new Date();
  const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

  const hasAnyClassified = issues.some(i => i.classification_gate1 && i.classification_gate1 !== 'pending');

  const metrics = useMemo(() => {
    // 1. Queue Growth Rate
    const createdLast7 = issues.filter(i => i.created_date && new Date(i.created_date) >= sevenDaysAgo).length;
    const resolvedLast7 = issues.filter(i => i.status === 'resolved' && i.updated_date && new Date(i.updated_date) >= sevenDaysAgo).length;
    const queueGrowth = {
      intake: createdLast7,
      resolution: resolvedLast7,
      status: getStatus(createdLast7, () => resolvedLast7 >= createdLast7, () => false),
    };

    // 2. Noise Reclassification Rate
    const totalNoise = issues.filter(i => i.classification_gate1 === 'noise').length;
    const noiseToSignal = issues.filter(i => 
      i.classification_gate1 === 'signal' && i.status === 'archived'
    ).length;
    // Also count signals that were previously noise (if gate1_classified_at exists and was later changed)
    // Simple proxy: signals with archived status hint at reclassification
    const noiseReclassRate = totalNoise + noiseToSignal > 0 ? (noiseToSignal / (totalNoise + noiseToSignal)) * 100 : null;
    const noiseReclass = {
      value: noiseReclassRate,
      total: totalNoise + noiseToSignal,
      reclassified: noiseToSignal,
      status: noiseReclassRate === null ? 'none' : getStatus(noiseReclassRate, v => v < 10, v => v < 20),
    };

    // 3. Signal Misclassification Rate
    const totalBalloons = issues.filter(i => i.classification_gate2 === 'balloon').length;
    const balloonToRock = issues.filter(i =>
      i.classification_gate2 === 'rock' && i.gate1_score !== undefined && i.gate2_score !== undefined
    ).length;
    // Simple proxy: we count rocks that might have been originally balloon 
    // More accurate: rocks with low gate2_score might indicate reclassification
    const signalMisclassRate = totalBalloons > 0 ? 0 : null; // Without history tracking, default to 0
    const signalMisclass = {
      value: signalMisclassRate,
      status: signalMisclassRate === null ? 'none' : getStatus(signalMisclassRate, v => v < 15, v => v < 25),
    };

    // 4. Bridging Policy Age
    const bridgingPolicies = policies.filter(p => 
      p.interim_for_capability_development && p.status !== 'retired' && p.designed_at
    );
    const avgBridgingAge = bridgingPolicies.length > 0 
      ? bridgingPolicies.reduce((sum, p) => sum + differenceInDays(now, new Date(p.designed_at)), 0) / bridgingPolicies.length
      : null;
    const bridgingAge = {
      value: avgBridgingAge,
      count: bridgingPolicies.length,
      status: avgBridgingAge === null ? 'none' : getStatus(avgBridgingAge, v => v < 90, v => v < 120),
    };

    // 5. Capability Mission Completion Rate
    const nonDeprecated = capabilities.filter(c => c.status !== 'deprecated');
    const activeCaps = capabilities.filter(c => c.status === 'active').length;
    const capCompRate = nonDeprecated.length > 0 ? (activeCaps / nonDeprecated.length) * 100 : null;
    const capCompletion = {
      value: capCompRate,
      active: activeCaps,
      total: nonDeprecated.length,
      status: capCompRate === null ? 'none' : getStatus(capCompRate, v => v >= 70, v => v >= 50),
    };

    // 6. Time to Resolution (Balloons)
    const resolvedBalloons = issues.filter(i => 
      i.classification_gate2 === 'balloon' && i.status === 'resolved' && i.gate2_classified_at
    );
    const avgBalloonTTR = resolvedBalloons.length > 0
      ? resolvedBalloons.reduce((sum, i) => {
          const classifiedAt = new Date(i.gate2_classified_at);
          const resolvedAt = new Date(i.updated_date || i.gate2_classified_at);
          return sum + Math.max(0, differenceInDays(resolvedAt, classifiedAt));
        }, 0) / resolvedBalloons.length
      : null;
    const balloonTTR = {
      value: avgBalloonTTR,
      count: resolvedBalloons.length,
      status: avgBalloonTTR === null ? 'none' : getStatus(avgBalloonTTR, v => v < 14, v => v < 21),
    };

    // 7. Time to First Mitigation (Rocks)
    const rocks = issues.filter(i => 
      i.classification_gate2 === 'rock' && i.gate2_classified_at
    );
    const rocksWithMitigation = rocks.filter(i => 
      i.rock_track3_status === 'in_progress' || i.rock_track3_status === 'completed'
    );
    const avgRockTTM = rocksWithMitigation.length > 0
      ? rocksWithMitigation.reduce((sum, i) => {
          const classifiedAt = new Date(i.gate2_classified_at);
          const mitigatedAt = new Date(i.updated_date || i.gate2_classified_at);
          return sum + Math.max(0, differenceInDays(mitigatedAt, classifiedAt));
        }, 0) / rocksWithMitigation.length
      : null;
    const rockTTM = {
      value: avgRockTTM,
      mitigated: rocksWithMitigation.length,
      total: rocks.length,
      status: avgRockTTM === null ? 'none' : getStatus(avgRockTTM, v => v < 7, v => v < 14),
    };

    // 8. Upward Report Action Rate
    const sessionsWithUpward = councilSessions.filter(s => s.upward_report && s.upward_report.trim());
    const acknowledgedSessions = sessionsWithUpward.filter(s => s.upward_report_acknowledged);
    const upwardActionRate = sessionsWithUpward.length > 0 
      ? (acknowledgedSessions.length / sessionsWithUpward.length) * 100 
      : null;
    const upwardAction = {
      value: upwardActionRate,
      acknowledged: acknowledgedSessions.length,
      total: sessionsWithUpward.length,
      status: upwardActionRate === null ? 'none' : getStatus(upwardActionRate, v => v >= 50, v => v >= 30),
    };

    // 9. Officer Reporting Participation
    const humanOfficers = officers.filter(o => !o.is_bot);
    const recentReporters = new Set(
      issues.filter(i => i.reported_by && i.created_date && new Date(i.created_date) >= sevenDaysAgo)
        .map(i => i.reported_by)
    );
    const participationRate = humanOfficers.length > 0 
      ? (recentReporters.size / humanOfficers.length) * 100 
      : null;
    const officerParticipation = {
      value: participationRate,
      reporters: recentReporters.size,
      total: humanOfficers.length,
      status: participationRate === null ? 'none' : getStatus(participationRate, v => v >= 80, v => v >= 60),
    };

    return { queueGrowth, noiseReclass, signalMisclass, bridgingAge, capCompletion, balloonTTR, rockTTM, upwardAction, officerParticipation };
  }, [issues, policies, capabilities, officers, councilSessions, sevenDaysAgo]);

  // Last session info
  const lastSession = useMemo(() => {
    if (councilSessions.length === 0) return null;
    const sorted = [...councilSessions].sort((a, b) => new Date(b.session_date || b.created_date) - new Date(a.session_date || a.created_date));
    return sorted[0];
  }, [councilSessions]);

  if (!hasAnyClassified) {
    return (
      <div className="text-center py-8 px-4 bg-slate-800/30 rounded-lg border border-slate-700/50">
        <Shield className="w-8 h-8 text-slate-600 mx-auto mb-3" />
        <p className="text-slate-400 text-sm">No High Council sessions have been conducted yet.</p>
        <p className="text-slate-500 text-xs mt-1">Metrics will populate as issues are classified through the ritual process.</p>
        <Link to={createPageUrl('highcouncil')}>
          <span className="text-indigo-400 text-xs mt-3 inline-block hover:underline">Go to High Council →</span>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Last Session Mini-Card */}
      {lastSession && (
        <Link to={createPageUrl('highcouncil')}>
          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200 hover:border-indigo-500/30 transition-colors">
            <div className="flex items-center gap-3">
              <Calendar className="w-4 h-4 text-indigo-400" />
              <div>
                <span className="text-xs text-slate-700">Last Session: </span>
                <span className="text-xs text-slate-700 font-medium">
                  {lastSession.session_date ? format(new Date(lastSession.session_date), 'MMM d, yyyy') : format(new Date(lastSession.created_date), 'MMM d, yyyy')}
                </span>
              </div>
              <Badge className="bg-slate-200 text-slate-700 text-[10px]">
                {(lastSession.issues_reviewed || []).length} issues
              </Badge>
              <Badge className={`text-[10px] ${lastSession.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                {lastSession.status}
              </Badge>
            </div>
            <ArrowRight className="w-4 h-4 text-slate-700" />
          </div>
        </Link>
      )}

      {/* 3x3 Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {/* 1. Queue Growth */}
        <MetricCardItem
          metric={METRIC_DEFS[0]}
          displayValue={`${metrics.queueGrowth.resolution} / ${metrics.queueGrowth.intake}`}
          targetLabel="Resolved / Created (7d) — Green: resolved ≥ created"
          status={metrics.queueGrowth.status}
        />

        {/* 2. Noise Reclassification */}
        <MetricCardItem
          metric={METRIC_DEFS[1]}
          displayValue={metrics.noiseReclass.value !== null ? `${metrics.noiseReclass.value.toFixed(1)}%` : 'N/A'}
          targetLabel="Target: < 10% (green), < 20% (amber)"
          status={metrics.noiseReclass.status}
        />

        {/* 3. Signal Misclassification */}
        <MetricCardItem
          metric={METRIC_DEFS[2]}
          displayValue={metrics.signalMisclass.value !== null ? `${metrics.signalMisclass.value.toFixed(1)}%` : 'N/A'}
          targetLabel="Target: < 15% (green), < 25% (amber)"
          status={metrics.signalMisclass.status}
        />

        {/* 4. Bridging Policy Age */}
        <MetricCardItem
          metric={METRIC_DEFS[3]}
          displayValue={metrics.bridgingAge.value !== null ? `${Math.round(metrics.bridgingAge.value)}d` : 'N/A'}
          targetLabel={`${metrics.bridgingAge.count} policies — Target: < 90d (green), < 120d (amber)`}
          status={metrics.bridgingAge.status}
        />

        {/* 5. Capability Completion */}
        <MetricCardItem
          metric={METRIC_DEFS[4]}
          displayValue={metrics.capCompletion.value !== null ? `${Math.round(metrics.capCompletion.value)}%` : 'N/A'}
          targetLabel={`${metrics.capCompletion.active}/${metrics.capCompletion.total} — Target: ≥ 70% (green), ≥ 50% (amber)`}
          status={metrics.capCompletion.status}
        />

        {/* 6. Balloon TTR */}
        <MetricCardItem
          metric={METRIC_DEFS[5]}
          displayValue={metrics.balloonTTR.value !== null ? `${Math.round(metrics.balloonTTR.value)}d` : 'N/A'}
          targetLabel={`${metrics.balloonTTR.count} resolved — Target: < 14d (green), < 21d (amber)`}
          status={metrics.balloonTTR.status}
        />

        {/* 7. Rock TTM */}
        <MetricCardItem
          metric={METRIC_DEFS[6]}
          displayValue={metrics.rockTTM.value !== null ? `${Math.round(metrics.rockTTM.value)}d` : 'N/A'}
          targetLabel={`${metrics.rockTTM.mitigated}/${metrics.rockTTM.total} mitigated — Target: < 7d (green), < 14d (amber)`}
          status={metrics.rockTTM.status}
        />

        {/* 8. Upward Action Rate */}
        <MetricCardItem
          metric={METRIC_DEFS[7]}
          displayValue={metrics.upwardAction.value !== null ? `${Math.round(metrics.upwardAction.value)}%` : 'N/A'}
          targetLabel={`${metrics.upwardAction.acknowledged}/${metrics.upwardAction.total} — Target: ≥ 50% (green), ≥ 30% (amber)`}
          status={metrics.upwardAction.status}
        />

        {/* 9. Officer Participation */}
        <MetricCardItem
          metric={METRIC_DEFS[8]}
          displayValue={metrics.officerParticipation.value !== null ? `${Math.round(metrics.officerParticipation.value)}%` : 'N/A'}
          targetLabel={`${metrics.officerParticipation.reporters}/${metrics.officerParticipation.total} officers — Target: ≥ 80% (green), ≥ 60% (amber)`}
          status={metrics.officerParticipation.status}
        />
      </div>
    </div>
  );
}